/*
 * Copyright 2020-2021 AskChristee, LLC. All rights reserved.
 */

import React, { useContext, useEffect, useRef, useState } from 'react';
import { Accordion, Tab, Tabs } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import {hINVESTMENT} from '../../../../api';
import {
  FormInputCreditScore,
  FormInputDatePicker2,
  FormInputDollar,
  FormInputEmail,
  FormInputNumber,
  FormInputPercent2,
  FormInputRadioButtons,
  FormInputStateAreaDropdowns,
  FormInputStateAreaDropdownsHandle,
  FormInputTerm,
  FormInputTermHandle,
  FormInputText,
  FormInputZip, FormLoader, FormWrapper,
  ValidationModal,
} from '../../../molecules';
import { Help } from '../../../molecules/QuickStart';
import {
  tipAll_cash,
  tipClose_date,
  tipCondo,
  tipCost_seg,
  tipCost_seg_period,
  tipCust_name,
  tipEmail,
  tipFiling_status,
  tipInput_dol_cred,
  tipInput_ltv,
  tipInput_ma,
  tipInput_uw_fee,
  tipInt_rate,
  tipInvestQ_income,
  tipLand_value,
  tipMain_amt,
  tipOrg_fee,
  tipPri_fin,
  tipProp_app,
  tipProp_mgt,
  tipProp_t,
  tipRent_income,
  tipRent_increase,
  tipReport_years,
  tipSales_cost,
  tipSp,
  tipS_cred,
  tipVac_allow,
  tipZip,
  UiSettingsProviderContext, PoweredBy,
} from '../../../atoms';
import { ChristeeConfigCtx } from '../../../context';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { toast } from 'react-toastify';
import { fetchInvest } from '../../../../api';
import { TInvestmentResponse } from '../../../../types/api';
import { ButtonSendPdf } from '../../../molecules/ButtonSendPdf';
import { InvestmentReport } from '../../Reports';
import ButtonDownloadPdf from "../../../molecules/ButtonDownloadPdf";
import { useRecommendations } from '../../../../hooks/useRecommendations';
import { Recommended } from '../../Recommendations';
import HomeMainImage from "../../../../images/main.png"

const Investment = () => {
  const defaultValues = {
    account_type: '',
    all_cash: 'n',
    close_date: '',
    condo: '',
    cost_seg: '',
    cost_seg_period: '',
    cust_email: '',
    cust_name: '',
    cust_phone: '',
    filing_status: 's',
    input_dol_cred: '',
    input_ltv: '',
    input_ma: '',
    input_uw_fee: '',
    inputcred: '',
    int_rate: '',
    land_value: '',
    main_amt: '',
    org_fee: '',
    priv_fin: 'n',
    prop_app: '',
    prop_mgt: '',
    prop_t: '',
    q_income: '',
    rent_income: '',
    rent_increase: '',
    report_years: '',
    s_cred: '',
    sales_cost: '',
    sp: '',
    vac_allow: '',
  };
  const {
    control,
    handleSubmit,
    watch,
    reset,
    getValues,
    setValue,
    trigger,
    formState: { errors, isValid, touchedFields, submitCount },
  } = useForm<any>({ mode: 'onTouched', reValidateMode: 'onBlur', defaultValues });
  const { executeRecaptcha } = useGoogleReCaptcha();
  const { currentSettings } = useContext(UiSettingsProviderContext);
  const { recommendations, requestRecommendations } = useRecommendations();

  const scrollTop = () => {
    // @ts-ignore
    window.scrollTo({ top: 0, behavior: 'instant' });
  };

  useEffect(() => {
    console.log('useEffect->scrollTop');
    scrollTop();
  }, []);

  const [dats, setDats] = useState<TInvestmentResponse>();
  const [loaded, setLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [key, setKey] = useState<string | null>('cash');
  const ctx = useContext(ChristeeConfigCtx);
  const stateAreaRef = useRef<FormInputStateAreaDropdownsHandle>(null);
  const termRef = useRef<FormInputTermHandle>(null);

  const onSubmit = async (data: any) => {
    await trigger();
    scrollTop();
    setLoading(true);
    await new Promise((r) => setTimeout(r, 800));
    console.log('data', data);
    console.log('watch', watch);
    console.log('errors', errors);
    const doTypeArr = data.doType && data.doType.length > 0 ? data.doType.map((m: any) => m.value) : [];
    const filteredData = {
      ...data,
      state: data.state[0].code,
      county_select: data.county_select[0].code,
      doType: doTypeArr,
      // term: Array.isArray(data.term) ? data.term[0].value : TERM_OPTIONS.find((f) => f.text === data.term)?.value,
      term: Array.isArray(data.term) ? data.term[0].value : data.term ? data.term : '30',
    };

    try {
      let captchaToken;
      if (executeRecaptcha) {
        captchaToken = await executeRecaptcha();
      }
      const modulePromise = fetchInvest(
        {
          ...filteredData,
          company_name: currentSettings.companyId,
          logged_in_email: currentSettings.email,
          logged_in_name: currentSettings.name,
          logged_in_phone: currentSettings.phone,
          account_type: currentSettings.accountType,
        },
        ctx?.stage,
        ctx?.key,
        captchaToken
      );

      const recommendationPromise = requestRecommendations({
        zipCode: data.zip,
        state: data.state?.[0].text
      });

      const [res] = await Promise.all([modulePromise, recommendationPromise]);

      if (res.meta?.displayMessage) toast.info(res.meta.displayMessage);

      console.log('onSubmit->res', res);
      setDats(res);
      setLoaded(true);
      setLoading(false);
      setKey('cash');
      console.log('dats after setDats', dats);
      scrollTop();
    } catch (error) {
      setLoading(false);
      if (error instanceof Error) {
        toast.error(error.message);
      } else {
        toast.error('Something went wrong!');
      }
    }
  };

  const clearForm = (data: any) => {
    console.log('clearForm->data', data);
    console.log('clearForm->isValid', isValid);
    console.log('clearForm->errors', errors);
    console.log('clearForm->touchedFields', touchedFields);
    console.log('clearForm->submitCount', submitCount);
    console.log('clearForm->getValues->state', getValues('state'));
    stateAreaRef.current?.clear();
    termRef.current?.clear();
    reset(defaultValues);
    setDats({} as TInvestmentResponse);
    setLoaded(false);
    setLoading(false);
    setKey('cash');
    scrollTop();
  };

  return (
    <FormWrapper>
      <div className="buyers-choice">
        <div className="container-fluid">
          <div className="row g-2">
            <div className="col-lg-3">
              <h3 className="h2">Investment</h3>
              <form className="christy" autoComplete="off">
                <fieldset>
                  <legend className="sr-only">Required Fields:</legend>
                  <FormInputStateAreaDropdowns setValue={setValue} stateWatch={watch('state')} areaWatch={watch('county_select')} control={control} errors={errors} ref={stateAreaRef} />
                  <FormInputZip label="Zip Code" tip={tipZip} error={errors.zip} name="zip" control={control} required={true} />
                  <FormInputDollar label="Sales Price" tip={tipSp} error={errors.sp} name="sp" control={control} required={true} />
                  <FormInputDollar label="Land Value" tip={tipLand_value} error={errors.land_value} name="land_value" required={true} control={control} />
                  <FormInputPercent2 label="Interest Rate" tip={tipInt_rate} error={errors.int_rate} name="int_rate" required={true} control={control} />
                  <FormInputTerm watch={watch('term')} ref={termRef} control={control} errors={errors} required={true} />
                  <FormInputDollar label="Personal Annual Income" tip={tipInvestQ_income} error={errors.q_income} name="q_income" required={true} control={control} />
                  <FormInputDollar label="Monthly Rental Income" tip={tipRent_income} error={errors.rent_income} name="rent_income" control={control} required={true} />
                  <FormInputPercent2 label="Annual Rent Increase" tip={tipRent_increase} error={errors.rent_increase} name="rent_increase" control={control} required={true} />
                  <FormInputPercent2 label="Vacancy Allowance" tip={tipVac_allow} error={errors.vac_allow} name="vac_allow" required={true} control={control} />
                  <FormInputDollar label="Annual Maintenance" tip={tipMain_amt} error={errors.main_amt} name="main_amt" required={true} control={control} />
                  <FormInputPercent2 label="Management Fee" tip={tipProp_mgt} error={errors.prop_mgt} name="prop_mgt" required={true} control={control} />
                  <FormInputPercent2 label="Sales Cost" tip={tipSales_cost} error={errors.sales_cost} name="sales_cost" required={true} control={control} />
                  <FormInputNumber label="Years for Report" tip={tipReport_years} error={errors.report_years} name="report_years" required={true} control={control} />
                  <FormInputPercent2 label="Annual Appreciation" tip={tipProp_app} error={errors.prop_app} name="prop_app" required={true} control={control} />
                  <FormInputCreditScore error={errors.inputcred} control={control} required={true} />
                  <FormInputRadioButtons
                    label="Married"
                    name="filing_status"
                    tip={tipFiling_status}
                    defaultValue="s"
                    required={true}
                    ids={['married', 'single']}
                    values={['m', 's']}
                    control={control}
                    errors={errors}
                  />
                </fieldset>
                <Accordion id="optional-inputs">
                  <Accordion.Item eventKey="0">
                    <Accordion.Header data-cy="btn-optional">Optional Inputs</Accordion.Header>
                    <Accordion.Body>
                      <fieldset>
                        <FormInputDollar label="Annual Property Taxes" tip={tipProp_t} error={errors.prop_t} name="prop_t" control={control} />
                        <FormInputDatePicker2 label="Close Date" tip={tipClose_date} error={errors.close_date} name="close_date" control={control} />
                        <FormInputPercent2 label="Down Payment" tip={tipInput_ltv} error={errors.input_ltv} name="input_ltv" control={control} />
                        <FormInputDollar label="Limit Loan Amt" tip={tipInput_ma} error={errors.input_ma} name="input_ma" control={control} />
                        <FormInputPercent2 label="Seller Credit" tip={tipS_cred} error={errors.s_cred} name="s_cred" control={control} />
                        <FormInputDollar label="Seller credit" tip={tipInput_dol_cred} error={errors.input_dol_cred} name="input_dol_cred" control={control} />
                        <FormInputDollar label="Lender Fees" tip={tipInput_uw_fee} error={errors.input_uw_fee} name="input_uw_fee" control={control} />
                        <FormInputDollar label="Loan Origination" tip={tipOrg_fee} error={errors.org_fee} name="org_fee" control={control} />
                        <FormInputDollar label="Condo Fee" tip={tipCondo} error={errors.condo} name="condo" control={control} />
                        <FormInputRadioButtons label="All Cash" name="all_cash" tip={tipAll_cash} defaultValue="n" ids={['all_cash_yes', 'all_cash_no']} control={control} errors={errors} />
                        <FormInputRadioButtons label="Private Financing" name="pri_fin" tip={tipPri_fin} defaultValue="n" ids={['pri_fin_yes', 'pri_fin_no']} control={control} errors={errors} />
                        <FormInputDollar label="Cost Segregation Amt" tip={tipCost_seg} error={errors.cost_seg} name="cost_seg" control={control} />
                        <FormInputNumber label="Cost Segregation Period" tip={tipCost_seg_period} error={errors.cost_seg_period} name="cost_seg_period" control={control} />
                        <FormInputText label="Name" tip={tipCust_name} error={errors.cust_name} name="cust_name" control={control} />
                        <FormInputText label="Phone" tip={tipCust_name} error={errors.cust_phone} name="cust_phone" control={control} />
                        <FormInputEmail accountType={currentSettings.accountType} tip={tipEmail} error={errors.cust_email} control={control} />
                      </fieldset>
                    </Accordion.Body>
                  </Accordion.Item>
                </Accordion>
                <p className="mt-3">
                  <ValidationModal label="Submit" errors={errors} onClick={handleSubmit(onSubmit)} />
                  <button type="reset" className="btn btn-outline-secondary me-2" onClick={clearForm}>
                    Clear
                  </button>
                </p>
              </form>
            </div>
            <div className="col-lg-9 position-relative ps-4">
              {!loading && recommendations && <Recommended recommendations={recommendations} />}
              <div className="form-results">
                <div className="w-100 d-flex align-items-center justify-content-end mb-3">
                  <Help sFile={hINVESTMENT} />
                </div>
                {loading ? (
                  <FormLoader />
                ) : loaded && dats ? (
                  <>
                    <Tabs id="controlled-tab" activeKey={key as string} onSelect={(k) => setKey(k)} className="mb-3">
                      <Tab className="pt-3" eventKey="cash" title="Results">
                        <InvestmentReport {...dats!} />
                      </Tab>
                    </Tabs>

                    <div className="d-flex mt-3">
                      <ButtonDownloadPdf data={dats} fileName="investment" />
                      {dats?.meta.email && dats?.meta.email !== 'nil' && <ButtonSendPdf dats={dats} />}
                    </div>
                  </>
                ) : (
                  <>
                    <h4 id="house-title" className="h4">
                      You're seconds away from unlocking your Investment data.
                    </h4>
                    <img id="house-image" src={HomeMainImage} alt="home" />
                  </>
                )}
              </div>
              {/* only guests can see this */}

              <div className="w-100 mt-3">
                <PoweredBy />
              </div>
            </div>
          </div>
        </div>
      </div>
    </FormWrapper>
  );
};

export default Investment;
